// ---------------------------------------------------------------------------
// Slide-aware accessibility analyzer (WCAG 2.1 AA contrast + text size + alt).
//
// Computes accessibility issues for a slide or a deck from the structured
// SlideContentV1 model. The chip / panel / export preflight all consume this
// output. Nothing is persisted — every call is a fresh evaluation.
// ---------------------------------------------------------------------------

import type { ResolvedSlide, ResolvedSlideElement } from '@/shared/theming/resolve';
import {
  contrastRatio,
  isBoldWeight,
  isLargeText,
  isOpaque,
  pickBlackOrWhiteForBackground,
  pickBlackOrWhiteForText,
  requiredRatio,
} from '@/shared/utils/contrast';

// The checker runs on the fully-resolved view (§6.3c): concrete geometry,
// resolved style, decoration excluded. Logic is unchanged from before — only the
// parameter types narrow from the now-optional `Slide`/`SlideElement`.
type ResolvedText = Extract<ResolvedSlideElement, { type: 'text' }>;
type ResolvedShape = Extract<ResolvedSlideElement, { type: 'shape' }>;
type ResolvedImage = Extract<ResolvedSlideElement, { type: 'image' }>;

export type Severity = 'warn' | 'fail';

export type IssueKind =
  | 'text-contrast'
  | 'non-text-contrast'
  | 'unknown-background'
  | 'small-text'
  | 'missing-alt';

export type AccessibilityFix =
  | { kind: 'set-text-color'; color: '#000000' | '#FFFFFF'; label: string }
  | {
      kind: 'add-text-panel';
      backgroundColor: '#000000' | '#FFFFFF';
      padding: number;
      label: string;
    }
  | { kind: 'set-font-size'; fontSize: number; label: string }
  | { kind: 'set-shape-stroke'; color: '#000000' | '#FFFFFF'; label: string };

export interface AccessibilityIssue {
  /** Stable id within a deck — re-rendered identically when content hasn't changed. */
  id: string;
  slideId: string;
  elementId: string;
  kind: IssueKind;
  severity: Severity;
  ratio?: number;
  requiredRatio?: number;
  foreground?: string;
  background?: string;
  message: string;
  fixes: AccessibilityFix[];
}

/** Minimum text size we will not warn about (in points). */
export const MIN_TEXT_SIZE_PT = 18;
/** Default padding used by the "Add text panel" auto-fix. */
export const DEFAULT_TEXT_PANEL_PADDING = 8;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function checkDeck(slides: ResolvedSlide[]): Record<string, AccessibilityIssue[]> {
  const out: Record<string, AccessibilityIssue[]> = {};
  for (const slide of slides) {
    out[slide.id] = checkSlide(slide);
  }
  return out;
}

export function checkSlide(slide: ResolvedSlide): AccessibilityIssue[] {
  const issues: AccessibilityIssue[] = [];
  for (const el of slide.elements) {
    if (el.type === 'text') {
      issues.push(...checkTextElement(el, slide));
    } else if (el.type === 'shape') {
      issues.push(...checkShapeElement(el, slide));
    } else if (el.type === 'image') {
      issues.push(...checkImageAlt(el, slide));
    }
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Effective background resolution
// ---------------------------------------------------------------------------

type EffectiveBackground =
  | { kind: 'color'; color: string; source: 'text-panel' | 'shape' | 'slide' }
  | { kind: 'image' };

/**
 * Find what visually sits behind the element using the z-index model.
 * 1. For text: if the element has an opaque backgroundColor, that is the background.
 * 2. Otherwise, scan elements with lower zIndex that overlap. Take the topmost
 *    (highest zIndex) candidate:
 *    - shape with opaque fillColor → use that color
 *    - image → unknown background (reviewer signal)
 *    - transparent / no fill → continue downward
 * 3. Otherwise fall back to the slide background.
 */
export function findEffectiveBackground(
  el: ResolvedSlideElement,
  slide: ResolvedSlide
): EffectiveBackground {
  if (el.type === 'text' && isOpaque(el.style.backgroundColor)) {
    return { kind: 'color', color: el.style.backgroundColor!, source: 'text-panel' };
  }

  const candidates = slide.elements
    .filter((other) => other.id !== el.id)
    .filter((other) => other.position.zIndex < el.position.zIndex)
    .filter((other) => boxesOverlap(other.position, el.position))
    .sort((a, b) => b.position.zIndex - a.position.zIndex);

  for (const candidate of candidates) {
    if (candidate.type === 'shape') {
      // Lines do not act as a fill background; skip.
      if (candidate.content.shapeType === 'line') continue;
      if (isOpaque(candidate.content.fillColor)) {
        return { kind: 'color', color: candidate.content.fillColor!, source: 'shape' };
      }
      continue;
    }
    if (candidate.type === 'image') {
      return { kind: 'image' };
    }
    // Other element types (text, interactive) are not treated as a background surface.
  }

  return { kind: 'color', color: slide.background.color ?? '#ffffff', source: 'slide' };
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}
/** True when two axis-aligned rectangles share more than an edge. */
function boxesOverlap(a: Rect, b: Rect): boolean {
  const ix = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const iy = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return ix > 0 && iy > 0;
}

// ---------------------------------------------------------------------------
// Per-element checks
// ---------------------------------------------------------------------------

function checkTextElement(el: ResolvedText, slide: ResolvedSlide): AccessibilityIssue[] {
  const issues: AccessibilityIssue[] = [];
  const fontSize = el.style.fontSize ?? 22;
  const large = isLargeText(fontSize, el.style.fontWeight);

  // Small text (independent of contrast)
  if (fontSize < MIN_TEXT_SIZE_PT) {
    issues.push({
      id: makeIssueId(slide.id, el.id, 'small-text'),
      slideId: slide.id,
      elementId: el.id,
      kind: 'small-text',
      severity: 'warn',
      message: `Text is ${fontSize}pt. Body text should be at least ${MIN_TEXT_SIZE_PT}pt to stay readable.`,
      fixes: [
        {
          kind: 'set-font-size',
          fontSize: MIN_TEXT_SIZE_PT,
          label: `Set to ${MIN_TEXT_SIZE_PT}pt`,
        },
      ],
    });
  }

  // Contrast (or unknown background)
  const bg = findEffectiveBackground(el, slide);
  const textColor = el.style.color ?? '#000000';

  if (bg.kind === 'image') {
    const panelColor = pickBlackOrWhiteForText(textColor);
    issues.push({
      id: makeIssueId(slide.id, el.id, 'unknown-background'),
      slideId: slide.id,
      elementId: el.id,
      kind: 'unknown-background',
      severity: 'warn',
      foreground: textColor,
      message:
        'Text sits over an image or complex background — contrast cannot be measured. Add a text panel or move the text.',
      fixes: [
        {
          kind: 'add-text-panel',
          backgroundColor: panelColor,
          padding: DEFAULT_TEXT_PANEL_PADDING,
          label: `Add ${panelColor === '#FFFFFF' ? 'white' : 'black'} panel behind text`,
        },
      ],
    });
    return issues;
  }

  const required = requiredRatio({ isLargeText: large, isNonText: false });
  const ratio = contrastRatio(textColor, bg.color);
  if (ratio === null) return issues;

  if (ratio < required) {
    issues.push({
      id: makeIssueId(slide.id, el.id, 'text-contrast'),
      slideId: slide.id,
      elementId: el.id,
      kind: 'text-contrast',
      severity: 'fail',
      ratio,
      requiredRatio: required,
      foreground: textColor,
      background: bg.color,
      message: `${large ? 'Large text' : 'Body text'} contrast is ${ratio.toFixed(2)}:1 — WCAG 2.1 AA requires at least ${required}:1.`,
      fixes: buildTextContrastFixes(bg.color, required),
    });
  }

  return issues;
}

function buildTextContrastFixes(bg: string, required: number): AccessibilityFix[] {
  const fixes: AccessibilityFix[] = [];
  const ratioBlack = contrastRatio('#000000', bg) ?? 0;
  const ratioWhite = contrastRatio('#FFFFFF', bg) ?? 0;
  const blackPasses = ratioBlack >= required;
  const whitePasses = ratioWhite >= required;

  // Prefer whichever passes; if both pass, lead with the higher-contrast option.
  const recommended = pickBlackOrWhiteForBackground(bg);
  const ordered: Array<'#000000' | '#FFFFFF'> =
    recommended === '#000000' ? ['#000000', '#FFFFFF'] : ['#FFFFFF', '#000000'];

  for (const color of ordered) {
    const passes = color === '#000000' ? blackPasses : whitePasses;
    if (!passes) continue;
    fixes.push({
      kind: 'set-text-color',
      color,
      label: `Use ${color === '#000000' ? 'black' : 'white'} text`,
    });
  }
  return fixes;
}

function checkShapeElement(el: ResolvedShape, slide: ResolvedSlide): AccessibilityIssue[] {
  const issues: AccessibilityIssue[] = [];
  const strokeWidth = el.content.strokeWidth ?? 0;
  const strokeColor = el.content.strokeColor;
  const isLine = el.content.shapeType === 'line';

  // Non-text contrast: only meaningful when there's an actual stroke (or for lines, always).
  if (!isLine && strokeWidth <= 0) return issues;
  if (!strokeColor) return issues;

  // Strokes are evaluated against the surface immediately behind the shape.
  // For lines that surface is the effective background. For rectangles/circles
  // with an opaque fill, the stroke sits at the boundary, so it must contrast
  // against the *adjacent* surface — we approximate that as the effective
  // background (what's beyond the shape's edge).
  const bg = findEffectiveBackground(el, slide);
  if (bg.kind === 'image') return issues; // skip — too noisy for a stroke on a photo

  const ratio = contrastRatio(strokeColor, bg.color);
  if (ratio === null) return issues;
  const required = requiredRatio({ isLargeText: false, isNonText: true });
  if (ratio >= required) return issues;

  issues.push({
    id: makeIssueId(slide.id, el.id, 'non-text-contrast'),
    slideId: slide.id,
    elementId: el.id,
    kind: 'non-text-contrast',
    severity: 'fail',
    ratio,
    requiredRatio: required,
    foreground: strokeColor,
    background: bg.color,
    message: `${isLine ? 'Line' : 'Shape outline'} contrast is ${ratio.toFixed(2)}:1 — WCAG 2.1 AA requires at least ${required}:1.`,
    fixes: [
      {
        kind: 'set-shape-stroke',
        color: pickBlackOrWhiteForBackground(bg.color),
        label: `Use ${pickBlackOrWhiteForBackground(bg.color) === '#000000' ? 'black' : 'white'} ${isLine ? 'line' : 'outline'}`,
      },
    ],
  });

  return issues;
}

function checkImageAlt(el: ResolvedImage, slide: ResolvedSlide): AccessibilityIssue[] {
  const alt = el.content.alt;
  // Treat unresolved placeholders as not-yet-an-image; the alt warning is for real images.
  const hasSource = Boolean(el.content.src || el.content.storagePath);
  if (!hasSource) return [];
  if (alt && alt.trim().length > 0) return [];
  return [
    {
      id: makeIssueId(slide.id, el.id, 'missing-alt'),
      slideId: slide.id,
      elementId: el.id,
      kind: 'missing-alt',
      severity: 'warn',
      message: 'Image is missing alt text. Screen readers will skip it without a description.',
      fixes: [],
    },
  ];
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

function makeIssueId(slideId: string, elementId: string, kind: IssueKind): string {
  return `${slideId}:${elementId}:${kind}`;
}

/** Worst severity in the list — used by the chip badge. `null` when empty. */
export function worstSeverity(issues: AccessibilityIssue[]): Severity | null {
  if (issues.length === 0) return null;
  return issues.some((i) => i.severity === 'fail') ? 'fail' : 'warn';
}

// Re-export to keep the call site convenient.
export { isBoldWeight };
